/**
 * ─── LOYALTY CART SAVINGS COMPONENT JAVASCRIPT ───
 * Handles dynamic updates of the loyalty savings banner in cart drawer
 * Recalculates savings when cart items change
 * Fetches loyalty prices from cart drawer's hidden JSON data
 */

class LoyalMantaCartSavings extends HTMLElement {
  constructor() {
    super();
    
    // Get configuration from data attributes set by Liquid
    this.marketCountry = this.dataset.marketCountry || '';
    this.marketCountryCode = this.dataset.marketCountryCode || '';
    this.vatMultiplier = parseFloat(this.dataset.vatMultiplier || '1.0');
    this.customerLoggedIn = this.dataset.customerLoggedIn === 'true';
    this.customerHasLoyalty = this.dataset.customerHasLoyalty === 'true';
    this.customerCountryCode = this.dataset.customerCountryCode || '';
    this.currencyCodeEnabled = this.dataset.currencyCodeEnabled === 'true';
    
    this.debouncedUpdate = this.debounce(this.updateSavings.bind(this), 100);
    this.init();
  }

  init() {
    // Initial calculation
    this.updateSavings();
    
    // Listen for when cart drawer sections are re-rendered
    // This happens when items are added/removed/updated
    this.observeCartChanges();
  }

  observeCartChanges() {
    // Watch for changes to the cart drawer items container
    const cartDrawerItemsContainer = document.querySelector('.cart-drawer__items');
    
    if (cartDrawerItemsContainer) {
      const observer = new MutationObserver((mutations) => {
        // Check if cart items or the loyalty data script changed
        const hasRelevantChanges = mutations.some(mutation => {
          return mutation.type === 'childList' || 
                 (mutation.type === 'characterData' && mutation.target.parentElement?.id === 'CartDrawer-LoyaltyData');
        });
        
        if (hasRelevantChanges) {
          this.debouncedUpdate();
        }
      });
      
      observer.observe(cartDrawerItemsContainer, {
        childList: true,
        subtree: true,
        characterData: true
      });
    }
    
    // Watch for changes to the main cart page items container
    const mainCartItemsContainer = document.querySelector('#main-cart-items');
    
    if (mainCartItemsContainer) {
      const observer = new MutationObserver((mutations) => {
        const hasRelevantChanges = mutations.some(mutation => {
          return mutation.type === 'childList' || 
                 (mutation.type === 'characterData' && mutation.target.parentElement?.id === 'MainCart-LoyaltyData');
        });
        
        if (hasRelevantChanges) {
          this.debouncedUpdate();
        }
      });
      
      observer.observe(mainCartItemsContainer, {
        childList: true,
        subtree: true,
        characterData: true
      });
    }
    
    // Also listen for cart drawer opening
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer) {
      const drawerObserver = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.attributeName === 'class') {
            const classList = cartDrawer.classList;
            if (classList.contains('animate') && classList.contains('active')) {
              this.debouncedUpdate();
            }
          }
        });
      });
      
      drawerObserver.observe(cartDrawer, { attributes: true });
    }
  }

  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  mantaDelayedRedirect(event, url) {
    // Prevent immediate navigation
    event.preventDefault();
    event.stopPropagation();
    
    // Add hover class to trigger animation
    const mantaDelayButton = event.currentTarget;
    mantaDelayButton.classList.add('trigger-hover');
    
    // Wait for animation to complete, then redirect
    setTimeout(() => {
      window.location.href = url;
    }, 400);
  }

  async updateSavings() {
    try {
      // Check for loyalty data from both cart drawer and main cart page
      let loyaltyDataElement = document.getElementById('CartDrawer-LoyaltyData');
      
      // If not in cart drawer, check for main cart page
      if (!loyaltyDataElement) {
        loyaltyDataElement = document.getElementById('MainCart-LoyaltyData');
      }
      
      if (!loyaltyDataElement) {
        this.hide();
        return;
      }
      
      let loyaltyPriceData = {};
      try {
        loyaltyPriceData = JSON.parse(loyaltyDataElement.textContent);
      } catch (e) {
        console.error('Error parsing loyalty data:', e);
        this.hide();
        return;
      }
      
      // Check if there are any items
      if (Object.keys(loyaltyPriceData).length === 0) {
        this.hide();
        return;
      }
      
      // Calculate total savings from the loyalty data
      const totalSavings = this.calculateTotalSavings(loyaltyPriceData);
      
      // Update the banner UI
      this.updateUI(totalSavings);
      
    } catch (error) {
      console.error('Error updating loyalty cart savings:', error);
      this.hide();
    }
  }

  calculateTotalSavings(loyaltyPriceData) {
    let totalSavings = 0;
    let hasAnyLoyaltyPrice = false;
    
    // Loop through each variant in the cart
    for (const [variantId, variantData] of Object.entries(loyaltyPriceData)) {
      try {
        if (!variantData.loyalty_discounts || !this.marketCountry) {
          continue;
        }
        
        const loyaltyDiscounts = variantData.loyalty_discounts;
        const marketData = loyaltyDiscounts[this.marketCountry];
        
        if (marketData && marketData.status === 'active' && marketData.price > 0) {
          hasAnyLoyaltyPrice = true;
          
          // Convert loyalty price from EUR to cents and apply VAT
          const loyaltyPriceCents = Math.round(marketData.price * 100);
          const loyaltyPriceWithVat = Math.round(loyaltyPriceCents * this.vatMultiplier);
          
          // Get the ORIGINAL regular price from variant (not the cart price)
          const regularPrice = variantData.price; // Original variant price in cents
          const regularPriceWithVat = Math.round(regularPrice * this.vatMultiplier);
          
          // ALWAYS compare against regular price, not cart price
          // The cart price might be discounted, but we want to show savings from regular price
          const savingsPerItem = regularPriceWithVat - loyaltyPriceWithVat;
          
          // Multiply by quantity
          if (savingsPerItem > 0) {
            totalSavings += savingsPerItem * variantData.quantity;
          }
        }
      } catch (error) {
        console.error('Error calculating savings for variant:', variantId, error);
      }
    }
    
    return hasAnyLoyaltyPrice && totalSavings > 0 ? totalSavings : 0;
  }

  updateUI(totalSavings) {
    // Check for country mismatch or missing phone number scenarios
    const hasCountryMismatch = this.customerHasLoyalty && 
                                this.customerCountryCode && 
                                this.marketCountryCode && 
                                this.customerCountryCode !== this.marketCountryCode;
    
    const missingPhoneNumber = this.customerHasLoyalty && !this.customerCountryCode;
    
    // Only show error messages if there are actual savings to miss out on
    if (totalSavings > 0) {
      if (hasCountryMismatch) {
        this.showCountryMismatchError(totalSavings);
        return;
      }
      
      if (missingPhoneNumber) {
        this.showMissingPhoneError(totalSavings);
        return;
      }
    }
    
    // If no savings, hide the banner
    if (totalSavings <= 0) {
      this.hide();
      return;
    }
    
    // Format the savings amount
    const formattedAmount = this.formatMoney(totalSavings);
    
    // Determine message and button based on customer status
    let messageKey, ctaText, ctaUrl;
    
    if (!this.customerLoggedIn) {
      messageKey = 'not_logged_in';
      ctaText = null;
      ctaUrl = null;
    } else if (this.customerHasLoyalty) {
      messageKey = 'you_are_saving';
      ctaText = null;
      ctaUrl = null;
    } else {
      messageKey = 'update_profile';
      ctaText = this.getTranslation('edit_profile', 'Edit Profile');
      ctaUrl = '/tools/customr/edit-account?loyalty-edit=1';
    }
    
    const messageText = this.getTranslation(messageKey, '')
      .replace('{{ amount }}', formattedAmount);
    
    // Update the inner HTML
    const iconUrl = 'https://cdn.shopify.com/s/files/1/0923/0997/9481/files/icon_ottensten-180x180.png?v=1756367961';
    const iconAlt = this.getTranslation('loyalty_icon_alt', 'Loyalty icon');
    
    let html = `
      <div class="loyal-manta-cart-savings__inner">
        <div class="loyal-manta-cart-savings__icon">
          <img 
            src="${iconUrl}" 
            alt="${iconAlt}" 
            loading="lazy"
            width="32"
            height="32"
          />
        </div>
        <div class="loyal-manta-cart-savings__content">
          <p class="loyal-manta-cart-savings__message">${messageText}</p>
    `;
    
    if (ctaText && ctaUrl) {
      html += `
          <button onclick="this.closest('loyal-manta-cart-savings').mantaDelayHandleButtonClick(event, '${ctaUrl}')" class="button manta-btt-slide-up" type="button">
            <span>${ctaText}</span>
          </button>
      `;
    }
    
    if (!this.customerLoggedIn) {
      const loginText = this.getTranslation('login', 'Login');
      const registerText = this.getTranslation('register', 'Register');
      const loginUrl = window.Shopify?.routes?.account_login_url || '/account/login';
      const registerUrl = window.Shopify?.routes?.account_register_url || '/account/register';
      html += `
          <div class="loyal-manta-cart-savings__cta-group">
            <button onclick="this.closest('loyal-manta-cart-savings').mantaDelayHandleButtonClick(event, '${loginUrl}')" class="button manta-btt-slide-up" type="button">
              <span>${loginText}</span>
            </button>
            <button onclick="this.closest('loyal-manta-cart-savings').mantaDelayHandleButtonClick(event, '${registerUrl}')" class="loyal-manta-cart-savings__register-link" type="button">
              ${registerText}
            </button>
          </div>
      `;
    }
    
    html += `
        </div>
      </div>
    `;
    
    this.innerHTML = html;
    
    // Update data attributes
    this.dataset.savings = totalSavings.toString();
    this.dataset.customerSaving = this.customerHasLoyalty.toString();
    
    // Show the banner
    this.show();
  }

  showCountryMismatchError(totalSavings) {
    // Get country name for the customer's registered country
    const countryNames = {
      'EE': 'Estonia',
      'LT': 'Lithuania',
      'LV': 'Latvia',
      'PL': 'Poland',
      'DK': 'Denmark',
      'UA': 'Ukraine'
    };
    
    const customerCountryName = countryNames[this.customerCountryCode] || this.customerCountryCode;
    const marketCountryName = this.marketCountry || countryNames[this.marketCountryCode] || this.marketCountryCode;
    
    const messageText = this.getTranslation('country_mismatch', 
      `Your loyalty account is registered in ${customerCountryName}. You cannot benefit from ${marketCountryName} loyalty prices. Please contact support or create new account.`
    ).replace('{{ customer_country }}', customerCountryName)
     .replace('{{ market_country }}', marketCountryName);
    
    const iconUrl = 'https://cdn.shopify.com/s/files/1/0923/0997/9481/files/icon_ottensten-180x180.png?v=1756367961';
    const iconAlt = this.getTranslation('loyalty_icon_alt', 'Loyalty icon');
    
    const html = `
      <div class="loyal-manta-cart-savings__inner loyal-manta-cart-savings__inner--error">
        <div class="loyal-manta-cart-savings__icon">
          <img 
            src="${iconUrl}" 
            alt="${iconAlt}" 
            loading="lazy"
            width="32"
            height="32"
          />
        </div>
        <div class="loyal-manta-cart-savings__content">
          <p class="loyal-manta-cart-savings__message">${messageText}</p>
        </div>
      </div>
    `;
    
    this.innerHTML = html;
    this.dataset.errorState = 'country-mismatch';
    this.show();
  }

  showMissingPhoneError(totalSavings) {
    const formattedAmount = this.formatMoney(totalSavings);
    
    const messageText = this.getTranslation('missing_phone', 
      `Add your phone number to your profile to save ${formattedAmount} with loyalty discount`
    ).replace('{{ amount }}', formattedAmount);
    
    const ctaText = this.getTranslation('edit_profile', 'Edit Profile');
    const ctaUrl = '/tools/customr/edit-account';
    
    const iconUrl = 'https://cdn.shopify.com/s/files/1/0923/0997/9481/files/icon_ottensten-180x180.png?v=1756367961';
    const iconAlt = this.getTranslation('loyalty_icon_alt', 'Loyalty icon');
    
    const html = `
      <div class="loyal-manta-cart-savings__inner loyal-manta-cart-savings__inner--error">
        <div class="loyal-manta-cart-savings__icon">
          <img 
            src="${iconUrl}" 
            alt="${iconAlt}" 
            loading="lazy"
            width="32"
            height="32"
          />
        </div>
        <div class="loyal-manta-cart-savings__content">
          <p class="loyal-manta-cart-savings__message">${messageText}</p>
          <button onclick="this.closest('loyal-manta-cart-savings').mantaDelayHandleButtonClick(event, '${ctaUrl}')" class="button manta-btt-slide-up" type="button">
            <span>${ctaText}</span>
          </button>
        </div>
      </div>
    `;
    
    this.innerHTML = html;
    this.dataset.errorState = 'missing-phone';
    this.show();
  }

  formatMoney(cents) {
    const amount = cents / 100;
    
    if (this.currencyCodeEnabled && window.Shopify?.currency) {
      // Format with currency code
      return new Intl.NumberFormat(window.Shopify.locale || 'en', {
        style: 'currency',
        currency: window.Shopify.currency.active,
      }).format(amount);
    } else {
      // Format without currency code (use shop's money format)
      const moneyFormat = window.Shopify?.money_format || '{{amount}}';
      const formattedAmount = amount.toFixed(2).replace('.', ',');
      return moneyFormat.replace('{{amount}}', formattedAmount);
    }
  }

  getTranslation(key, fallback) {
    // Try to get translation from window.theme.strings if available
    const translationPath = `sections.cart.loyalty_savings.${key}`;
    
    if (window.theme?.strings && window.theme.strings[translationPath]) {
      return window.theme.strings[translationPath];
    }
    
    // Fallback translations
    const fallbacks = {
      'not_logged_in': 'With a loyalty customer profile you can save: {{ amount }}',
      'register_login': 'Register / Login',
      'login': 'Login',
      'register': 'Register',
      'you_are_saving': 'You are saving {{ amount }} with your loyalty profile',
      'update_profile': 'You can save {{ amount }} with loyalty programme - edit your profile for this benefit',
      'edit_profile': 'Edit Profile',
      'loyalty_icon_alt': 'Loyalty icon',
      'country_mismatch': 'Your loyalty account is registered in {{ customer_country }}. You cannot benefit from {{ market_country }} loyalty prices. Please contact support or create new account.',
      'missing_phone': 'Add your phone number to your profile to save {{ amount }} with loyalty discount'
    };
    
    return fallbacks[key] || fallback;
  }

  mantaDelayHandleButtonClick(event, url) {
    // Set polling flag before leaving so the poller knows what tag to wait for on return
    if (url && url.includes('/tools/customr/edit-account')) {
      try {
        sessionStorage.setItem('mantaProfileUpdatePending', JSON.stringify({
          type: url.includes('loyalty-edit') ? 'loyalty' : 'business',
          initiatedAt: Date.now()
        }));
      } catch (e) {}
    }
    this.mantaDelayedRedirect(event, url);
  }

  show() {
    this.classList.remove('hidden');
    this.style.display = 'block';
  }

  hide() {
    this.classList.add('hidden');
    this.style.display = 'none';
  }

  disconnectedCallback() {
    // Cleanup observers if needed
  }
}

// Register the custom element
if (typeof customElements !== 'undefined' && !customElements.get('loyal-manta-cart-savings')) {
  customElements.define('loyal-manta-cart-savings', LoyalMantaCartSavings);
}
